import httpClient from './httpClient';

// Helper function to parse MongoDB ObjectId format
const parseFileData = (files) => {
  if (!Array.isArray(files)) return files;
  
  return files.map(file => ({
    ...file,
    _id: file._id?.$oid || file._id || null,
    created_at: file.created_at?.$date || file.created_at,
    updated_at: file.updated_at?.$date || file.updated_at,
  }));
};

// Helper function to format file size for display
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get file category based on extension
const getFileCategory = (filename) => {
  if (!filename) return 'unknown';
  
  const extension = filename.toLowerCase().split('.').pop();
  const categories = {
    image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'],
    document: ['pdf', 'doc', 'docx', 'txt', 'rtf'],
    spreadsheet: ['xls', 'xlsx', 'csv'],
    presentation: ['ppt', 'pptx'],
    archive: ['zip', 'rar', '7z', 'tar', 'gz'],
    video: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'],
    audio: ['mp3', 'wav', 'ogg', 'flac', 'aac'],
    code: ['js', 'css', 'html', 'py', 'java', 'cpp', 'c', 'json', 'xml']
  };
  
  for (const [category, extensions] of Object.entries(categories)) {
    if (extensions.includes(extension)) {
      return category;
    }
  }
  
  return 'other';
};

// Upload file and attach to entity
const uploadFile = async (file, entityType, entityId, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('entity_type', entityType);
  formData.append('entity_id', entityId);
  
  if (options.description) {
    formData.append('description', options.description);
  }
  
  if (options.tags) {
    const tagsString = Array.isArray(options.tags) ? options.tags.join(',') : options.tags;
    formData.append('tags', tagsString);
  }
  
  const response = await httpClient.post('/api/files/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: options.onUploadProgress,
  });
  
  return response.data;
};

// Download file by file ID
const downloadFile = async (fileId, filename) => {
  const response = await httpClient.get(`/api/files/${fileId}`, {
    responseType: 'blob',
  });
  
  // Create blob URL and trigger download
  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  return { success: true };
};

// Get file metadata
const getFileMetadata = async (fileId) => {
  const response = await httpClient.get(`/api/files/${fileId}/metadata`);
  return response.data;
};

// Delete file
const deleteFile = async (fileId) => {
  const response = await httpClient.delete(`/api/files/${fileId}`);
  return response.data;
};

// Update file metadata
const updateFileMetadata = async (fileId, updates) => {
  const response = await httpClient.put(`/api/files/${fileId}/metadata`, updates);
  return response.data;
};

// Get all files for an entity
const getEntityFiles = async (entityType, entityId, options = {}) => {
  const params = {
    page: options.page || 1,
    page_size: options.pageSize || 50,
    include_deleted: options.includeDeleted || false,
  };
  
  const response = await httpClient.get(`/api/files/entity/${entityType}/${entityId}`, { params });
  const data = response.data;
  
  // Parse file data to handle MongoDB ObjectId format
  if (data.attachments) {
    data.attachments = parseFileData(data.attachments);
    
    // Add display properties
    data.attachments = data.attachments.map(file => ({
      ...file,
      displaySize: formatFileSize(file.file_size || 0),
      categoryDisplay: getCategoryDisplay(file.category || getFileCategory(file.filename)),
      canPreview: canPreviewFile(file.filename),
    }));
  }
  
  return data;
};

// Search files
const searchFiles = async (searchCriteria) => {
  const response = await httpClient.post('/api/files/search', searchCriteria);
  const data = response.data;
  
  // Parse file data
  if (data.attachments) {
    data.attachments = parseFileData(data.attachments);
    
    // Add display properties
    data.attachments = data.attachments.map(file => ({
      ...file,
      displaySize: formatFileSize(file.file_size || 0),
      categoryDisplay: getCategoryDisplay(file.category || getFileCategory(file.filename)),
      canPreview: canPreviewFile(file.filename),
    }));
  }
  
  return data;
};

// Bulk delete files
const bulkDeleteFiles = async (fileIds) => {
  const response = await httpClient.post('/api/files/bulk-delete', { file_ids: fileIds });
  return response.data;
};

// Get file system info
const getFileSystemInfo = async () => {
  const response = await httpClient.get('/api/files/info');
  return response.data;
};

// Helper function to get category display name
const getCategoryDisplay = (category) => {
  const categoryNames = {
    image: '图片',
    document: '文档',
    spreadsheet: '表格',
    presentation: '演示文稿',
    archive: '压缩包',
    video: '视频',
    audio: '音频',
    code: '代码',
    other: '其他',
    unknown: '未知'
  };
  
  return categoryNames[category] || '其他';
};

// Helper function to check if file can be previewed
const canPreviewFile = (filename) => {
  if (!filename) return false;
  
  const extension = filename.toLowerCase().split('.').pop();
  const previewableExtensions = [
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', // Images
    'pdf', // PDF
    'txt', // Text files
  ];
  
  return previewableExtensions.includes(extension);
};

// Validate file before upload
const validateFile = (file, entityType = '', maxSizeMB = 50) => {
  const errors = [];
  
  if (!file) {
    errors.push('未选择文件');
    return { isValid: false, errors };
  }
  
  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    errors.push(`文件大小不能超过 ${maxSizeMB}MB`);
  }
  
  // Check file type for specific entity types
  const restrictedTypes = {
    products: ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx', 'xls', 'xlsx'],
    sales_orders: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png'],
    quotations: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png'],
    purchases: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png']
  };
  
  if (entityType && restrictedTypes[entityType]) {
    const extension = file.name.toLowerCase().split('.').pop();
    if (!restrictedTypes[entityType].includes(extension)) {
      errors.push(`${entityType} 不支持 .${extension} 格式的文件`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Get file icon based on category
const getFileIcon = (filename) => {
  const category = getFileCategory(filename);
  const icons = {
    image: 'image',
    document: 'description',
    spreadsheet: 'grid_on',
    presentation: 'slideshow',
    archive: 'archive',
    video: 'movie',
    audio: 'audiotrack',
    code: 'code',
    other: 'insert_drive_file',
    unknown: 'help_outline'
  };
  
  return icons[category] || 'insert_drive_file';
};

// Get product attachments by product MongoDB _id
const getProductAttachments = async (productMongoId) => {
  const response = await httpClient.get(`/api/files/entity/products/${productMongoId}`);
  const data = response.data;

  // Add display properties
  if (data.attachments) {
    data.attachments = data.attachments.map(file => ({
      ...file,
      displaySize: formatFileSize(file.file_size || 0),
      categoryDisplay: getCategoryDisplay(file.category || getFileCategory(file.filename)),
      canPreview: canPreviewFile(file.filename),
    }));
  }

  return data;
};

// Get attachment counts for multiple products (batch)
const getProductsAttachmentCounts = async (productIds) => {
  if (!productIds || productIds.length === 0) {
    return {};
  }

  const response = await httpClient.post('/api/files/products-attachment-counts', {
    product_ids: productIds
  });
  return response.data.counts || {};
};

// Bulk download all product attachments for an entity as ZIP
const bulkDownloadEntityProductAttachments = async (entityType, entityId, entityName) => {
  try {
    const response = await httpClient.post('/api/files/bulk-download-entity-products', {
      entity_type: entityType,
      entity_id: entityId
    }, {
      responseType: 'blob'
    });

    // Check if the response is actually an error (when responseType is 'blob', errors come as blobs too)
    if (response.data.type === 'application/json') {
      // If the blob is JSON, it might be an error response
      const text = await response.data.text();
      try {
        const errorData = JSON.parse(text);
        if (errorData.error) {
          // Create an error object that mimics Axios error structure
          const error = new Error(errorData.error);
          error.response = {
            status: response.status,
            data: errorData
          };
          throw error;
        }
      } catch (parseError) {
        // If parsing fails, continue with normal download
      }
    }

    // Trigger download
    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${entityName}-product-attachments.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Bulk download error:', error);

    // Enhance error message for specific cases
    if (error.response?.status === 404) {
      if (error.response?.data?.error?.toLowerCase().includes('no attachments')) {
        error.userMessage = '没有找到可下载的产品附件。请先为产品上传附件文件。';
      }
    }

    throw error;
  }
};

const fileService = {
  // Core file operations
  uploadFile,
  downloadFile,
  getFileMetadata,
  deleteFile,
  updateFileMetadata,

  // Entity file operations
  getEntityFiles,
  searchFiles,
  bulkDeleteFiles,

  // Product attachment operations
  getProductAttachments,
  getProductsAttachmentCounts,
  bulkDownloadEntityProductAttachments,

  // System info
  getFileSystemInfo,

  // Helper functions
  formatFileSize,
  getFileCategory,
  getCategoryDisplay,
  canPreviewFile,
  validateFile,
  getFileIcon,
  parseFileData,
};

export default fileService;